import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';
import { UtilityService } from '@app/core/services/utility.service';
import { CustomerPersonItem, EconomyService, PriceListItem } from '@app/services/economy.service';
import { CountryItem, DeliveryMethodItem, DeliveryTermItem, DepartmentItem, LogisticService, StockplaceItem } from '@app/services/logistic.service';
import { ProjectItem } from '@app/services/project.service';
import { FreightRateItem, FreightRateSetItem, PurchaseStatusItem, PurchHeadGet, PurchHeadGetParams, PurchHeadService } from '@app/services/purch-head.service';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, EMPTY, from, interval, Observer, Subject, Subscription, switchMap, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-purch-head-head',
    templateUrl: './purch-head-head.component.html',
    styleUrls: ['./purch-head-head.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class PurchHeadHeadComponent implements OnInit, OnDestroy {
    @Input()
    public params?: PurchHeadGetParams;

    public get?: PurchHeadGet;

    public customerPersons: CustomerPersonItem[] = [];

    public departments: DepartmentItem[] = [];

    public countries: CountryItem[] = [];

    public freightRateSets: FreightRateSetItem[] = [];

    public freightRates: FreightRateItem[] = [];

    public deliveryTerms: DeliveryTermItem[] = [];

    public deliveryMethods: DeliveryMethodItem[] = [];

    public pricelists: PriceListItem[] = [];

    public stockplaces: StockplaceItem[] = [];

    public purchaseStatuses: PurchaseStatusItem[] = [];

    public portalUsers: Item[] = [];

    public initiatedByList: Item[] = [];

    public updatingFreight = false;

    public cancellingRest = false;

    public convertingToSalesOrder = false;

    public saving = false;

    private save$ = new Subject<PurchHeadGet>();

    private destroy$ = new Subject<void>();

    private convertToSalesOrderProgressSubscription?: Subscription;

    public layout = new BehaviorSubject<3 | 2 | 1>(3);

    /**
     * Event emitted when lines have been changes from head, meaning any lines displayed anywhere should be updated upon this event.
     */
    @Output()
    public linesChanged = new EventEmitter<void>();

    constructor(
        private elementRef: ElementRef,
        private purchHeadService: PurchHeadService,
        private economy: EconomyService,
        private logistic: LogisticService,
        private modal: ModalService,
        private utility: UtilityService,
        private toast: ToastService,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef,
    ) {
        this.save$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(1500),
                switchMap((purchHead) =>
                    from(this.purchHeadService.savePurchHead(purchHead, false)).pipe(
                        catchError((error) => {
                            this.onSaveErrorResponseFromAutoSave(error);
                            return EMPTY;
                        }),
                    ),
                ),
            )
            .subscribe(this.getSaveObserver());

        this.layout
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(),
                debounceTime(500),
                switchMap(() => EMPTY),
            )
            .subscribe();
    }

    public async openSelectProject() {
        const response: ProjectItem = await this.modal.openStateDialog({ state: 'selectproject', params: `-/-` });

        if (!!response && !!this.get) {
            await this.loadGet();
            this.onCommonChange(response.item_id, 'project_keyno');
        }
    }

    private resizeObserver?: ResizeObserver;

    private lastWidth = 0;

    private observe() {
        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver((entries) => {
                const containerWidth = (entries[0].target as HTMLElement).offsetWidth;

                if (this.lastWidth !== 0 && Math.abs(containerWidth - this.lastWidth) < 50) return;

                this.lastWidth = containerWidth;

                if (containerWidth >= 1440) {
                    this.layout.next(3);
                } else if (containerWidth >= 992) {
                    this.layout.next(2);
                } else {
                    this.layout.next(1);
                }

                this.cdr.markForCheck();
            });

            this.resizeObserver.observe(this.elementRef.nativeElement);
        });
    }

    private getSaveObserver(): Partial<Observer<ErrorMessageResponse>> {
        return {
            next: this.onSaveResponseFromAutoSave,
        };
    }

    private onSaveResponseFromAutoSave = () => {
        this.toast.addToast({ id: `purchhead.save.success`, type: 'success', title: 'saved!', translateTitle: true, message: 'changes_have_been_saved', translateMessage: true });
        this.get!.ischanged = '0';
        this.saving = false;
        this.cdr.markForCheck();
    };

    private onSaveErrorResponseFromAutoSave = (error: unknown) => {
        if (!!error && typeof error === 'object' && 'errormessage' in error && typeof error.errormessage === 'string') {
            this.toast.addToast({ id: `purchhead.save.${error.errormessage}`, type: 'danger', title: 'could_not_save', translateTitle: true, message: error.errormessage });
        }
        this.saving = false;
        this.cdr.markForCheck();
    };

    public openTrackTraceUrl() {
        window.open(this.get?.tracktrace_url);
    }

    public async onSupplierPersNoChanged() {
        if (!!this.get) {
            const { pers_email, pers_telephones } = await this.purchHeadService.getPersonInfo({ pers_no: this.get.supplier_pers_no });

            this.get.supplier_pers_email = pers_email;
            this.get.supplier_pers_telephone = pers_telephones;

            this.onCommonChange();
        }
    }

    public async onDepartmentIdChanged() {
        if (!!this.get) {
            const { delivery_cust_no, delivery_cust_name, delivery_address } = await this.purchHeadService.getDepartment({ department_id: this.get.department_id });

            this.get.delivery_cust_no = delivery_cust_no;
            this.get.delivery_cust_name = delivery_cust_name;
            this.get.delivery_address = delivery_address;
            this.onCommonChange();
        }
    }

    public async onFreightRateSetKeynoChanged() {
        this.onCommonChange();
        this.loadFreightRates();
    }

    public async onFreightRateKeynoChanged() {
        if (!!this.get) {
            const { customs_last_rate_locval, freight_last_rate_locval, freight_main_rate_locval } = await this.purchHeadService.getFreightRate({ freightrate_keyno: this.get.freightrate_keyno });

            this.get.customs_last_rate_locval = customs_last_rate_locval;
            this.get.freight_last_rate_locval = freight_last_rate_locval;
            this.get.freight_main_rate_locval = freight_main_rate_locval;
            this.onCommonChange();
        }
    }

    public onValutaExchangeRateLockedChanged() {
        if (!!this.get) {
            this.get.valuta_exchangefactor_locked = this.utility.parseNumber(this.get.valuta_exchangerate_locked) / this.utility.parseNumber(this.get.order_valuta_pr);
            this.onCommonChange();
        }
    }

    public onCommonChange(value?: any, field?: keyof PurchHeadGet) {
        if (!!this.get) {
            this.get.ischanged = '1';

            // TODO: implement this
            // if (this.get.autosave === '1') {
            // this.saving = true;
            // this.save$.next(this.get);
            // }
        }

        this.cdr.markForCheck();
    }

    public async updateFreight() {
        if (!this.get) return;

        this.updatingFreight = true;
        this.cdr.markForCheck();

        try {
            await this.save();
            const { errorcode, errormessage } = await this.purchHeadService.updateFreight({ purch_internal_no: this.get.purch_internal_no });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.linesChanged.emit();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.updatingFreight = false;
            this.cdr.markForCheck();
        }
    }

    public async cancelRest() {
        if (!this.get) return;

        this.cancellingRest = true;
        this.cdr.markForCheck();

        try {
            const { errorcode, errormessage } = await this.purchHeadService.cancelRest({ purch_internal_no: this.get.purch_internal_no });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.loadGet();
            this.cancellingRest = false;
            this.cdr.markForCheck();
        }
    }

    public async convertToSalesOrder() {
        if (!this.get) return;

        this.convertingToSalesOrder = true;
        this.get.show_progress_text_purchorder_to_salesorder = '1';
        this.cdr.markForCheck();

        try {
            this.convertToSalesOrderProgressSubscription = interval(3000).pipe(takeUntil(this.destroy$)).subscribe({ next: this.setConvertToSalesOrderProgress });
            const { errorcode, errormessage } = await this.purchHeadService.convertPurchOrderToSalesOrder({ purch_internal_no: +this.get.purch_internal_no });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            }
        } finally {
            this.convertToSalesOrderProgressSubscription?.unsubscribe();
            delete this.convertToSalesOrderProgressSubscription;
            this.loadGet();
            this.convertingToSalesOrder = false;
            this.get.show_progress_text_purchorder_to_salesorder = '0';
            this.cdr.markForCheck();
        }
    }

    public setConvertToSalesOrderProgress = async () => {
        if (!!this.get) {
            const { progress_text_purchorder_to_salesorder } = await this.purchHeadService.getConvertToSalesOrderProgress({ purch_internal_no: +this.get.purch_internal_no });

            this.get.progress_text_purchorder_to_salesorder = progress_text_purchorder_to_salesorder;
            this.cdr.markForCheck();
        }
    };

    public save = async () => {
        if (!this.get) return;

        this.saving = true;
        this.cdr.markForCheck();

        try {
            await this.purchHeadService.savePurchHead(this.get);
            this.get!.ischanged = '0';
        } catch (error) {
            console.error(error);
            throw Error('Network call failed');
        } finally {
            this.saving = false;
            this.cdr.markForCheck();
        }
    };

    private async loadGet() {
        if (!!this.get) this.purchHeadService.setPurchHeadFromParams({ purch_internal_no: +this.get!.purch_internal_no });
        this.cdr.markForCheck();
    }

    private async loadCustomerPersons() {
        if (!!this.get) {
            this.customerPersons = await this.economy.getCustomerPersons({ cust_no: +this.get.supplier_no });
        }
    }

    private async loadDepartments() {
        if (!!this.get) {
            this.departments = await this.logistic.getDepartments(this.get.businessco_no);
        }
    }

    private async loadCountries() {
        this.countries = await this.logistic.getCountries();
    }

    private async loadFreightRateSets() {
        if (!!this.get) {
            this.freightRateSets = await this.purchHeadService.getFreightRateSets({ cust_no: this.get.supplier_no });
        }
    }

    private async loadFreightRates() {
        if (!!this.get) {
            this.freightRates = await this.purchHeadService.getFreightRates({ freightrateset_keyno: this.get.freightrateset_keyno });
        }
    }

    private async loadDeliveryTerms() {
        this.deliveryTerms = await this.logistic.getDeliveryTerms();
    }

    private async loadDeliveryMethods() {
        this.deliveryMethods = await this.logistic.getDeliveryMethods();
    }

    private async loadPricelists() {
        this.pricelists = await this.economy.getPriceLists();
    }

    private async loadStockplaces() {
        if (!!this.get) {
            this.stockplaces = await this.logistic.getStockplaces({ businessco_no: '' + this.get.businessco_no });
        }
    }

    private async loadPurchaseStatuses() {
        this.purchaseStatuses = await this.purchHeadService.getPurchaseStatuses();
    }

    private async loadPortalUsers() {
        this.portalUsers = await this.logistic.getPurchaserPortalUsers();
    }

    private async loadInitatedByList() {
        this.initiatedByList = await this.purchHeadService.getIniatedByList();
    }

    private loadStaticVariables() {
        this.loadCountries();
        this.loadDeliveryTerms();
        this.loadDeliveryMethods();
        this.loadPricelists();
        this.loadPurchaseStatuses();
        this.loadPortalUsers();
        this.loadInitatedByList();
    }

    ngOnInit(): void {
        this.observe();
        this.loadStaticVariables();
        this.purchHeadService.currentPurchHead$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (value) => {
                if (value) {
                    this.get = value;
                    this.loadCustomerPersons();
                    this.loadDepartments();
                    this.loadFreightRateSets();
                    this.loadFreightRates();
                    this.loadStockplaces();
                } else {
                    delete this.get;
                }

                this.cdr.markForCheck();
            },
        });
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
